import { useEffect, useRef } from "react";
import { matchPath, useNavigate } from "react-router-dom";
import {
  useNotifications,
  type Notification,
} from "../../hooks/useNotifications";

// Mirror of the routes a notification may deep-link to (apps/web/src/App.tsx).
// The app's "*" catch-all redirects anything unknown to Home, so without this
// guard a stale or mistyped deepLink (old rows from before a route rename,
// admin free-text) bounces the user to Home with no explanation. Unknown
// links mark the row read and close the panel, but stay put.
const DEEP_LINKABLE_ROUTES = [
  "/",
  "/find-events",
  "/find-clarity",
  "/find-community",
  "/find-direction",
  "/find-practitioners",
  "/find-your-signs",
  "/journal",
  "/reading",
  "/profile",
  "/decks",
  "/upgrade",
  "/community",
  "/moon-calendar",
  "/moon-sign-list",
  "/live",
  "/cards",
  "/sky",
  "/pull-a-card",
  "/pricing",
  "/services",
  "/help",
  "/about",
  "/how-it-works",
  "/astrological-weather",
  "/beta-feedback",
  "/onboarding",
  "/terms",
  "/practitioners/:slug",
  "/admin/*",
];

// Deep-link shapes older notification rows carry from before this guard
// existed — none of them was ever a real route (they all bounced to Home).
// Remapping them makes historical notifications work for the first time.
const LEGACY_DEEPLINK_REMAP: Array<{ pattern: string; to: string }> = [
  { pattern: "/live/:id", to: "/find-events" },
  { pattern: "/find-cards", to: "/find-clarity" },
  { pattern: "/journal/:id", to: "/journal" },
];

// matchPath expects a bare pathname — strip query/hash for matching but keep
// the original link for navigation so params survive.
const pathnameOf = (link: string) => link.split(/[?#]/)[0];

export function isKnownDeepLink(link: string): boolean {
  return DEEP_LINKABLE_ROUTES.some((pattern) => matchPath(pattern, pathnameOf(link)) !== null);
}

/** Where a notification tap should go: the link itself, a legacy remap, or nowhere. */
export function resolveDeepLink(link: string): string | null {
  if (isKnownDeepLink(link)) return link;
  const legacy = LEGACY_DEEPLINK_REMAP.find((l) => matchPath(l.pattern, pathnameOf(link)) !== null);
  return legacy ? legacy.to : null;
}

const KIND_ICON: Record<Notification["kind"], string> = {
  ceremony: "●",
  daily_card: "✦",
  deck_launch: "◆",
  journal_save: "❋",
  practitioner_join: "❖",
  community_pull: "◌",
};

function timeAgo(iso: string): string {
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  const m = Math.floor(diff / 60_000);
  if (m < 1) return "now";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}

interface NotificationCenterProps {
  onClose: () => void;
}

export function NotificationCenter({ onClose }: NotificationCenterProps) {
  const navigate = useNavigate();
  const { notifications, unreadCount, markRead, markAllRead } =
    useNotifications();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [onClose]);

  const onRowClick = (n: Notification) => {
    markRead(n.id);
    const dest = n.deepLink ? resolveDeepLink(n.deepLink) : null;
    if (dest) navigate(dest);
    onClose();
  };

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label="Notifications"
      className="absolute right-0 top-full mt-2 w-80 max-w-[calc(100vw-2rem)] max-h-[70vh] overflow-y-auto bg-bg-card border border-[color:var(--hairline)] rounded-xl shadow-xl z-50"
    >
      <header className="sticky top-0 flex items-center justify-between px-4 py-3 bg-bg-card border-b border-[color:var(--hairline)]">
        <span className="text-[0.7rem] tracking-[0.2em] uppercase text-text-secondary">
          Notifications
        </span>
        {unreadCount > 0 ? (
          <button
            type="button"
            onClick={() => markAllRead()}
            className="text-xs text-accent-fg hover:underline"
          >
            Mark all read
          </button>
        ) : null}
      </header>
      {notifications.length === 0 ? (
        <p className="px-6 py-10 text-center text-sm text-text-muted italic">
          You're all caught up.
        </p>
      ) : (
        <ul className="divide-y divide-[color:var(--hairline)]">
          {notifications.map((n) => (
            <li key={n.id}>
              <button
                type="button"
                onClick={() => onRowClick(n)}
                className="w-full text-left px-4 py-3 flex gap-3 hover:bg-bg-surface"
              >
                <span
                  aria-hidden
                  className="text-lg leading-none mt-0.5 text-accent-fg"
                >
                  {KIND_ICON[n.kind]}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-sm font-medium text-text-primary truncate">
                      {n.title}
                    </span>
                    <span className="text-[0.65rem] text-text-muted shrink-0">
                      {timeAgo(n.createdAt)}
                    </span>
                  </div>
                  <p className="text-xs text-text-secondary mt-0.5 line-clamp-2">
                    {n.body}
                  </p>
                </div>
                {n.readAt === null ? (
                  <span
                    aria-hidden
                    className="mt-1.5 w-1.5 h-1.5 rounded-full bg-accent-fg shrink-0"
                  />
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
